import useMediaQuery from '@material-ui/core/useMediaQuery';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { findIndex, propEq } from 'ramda';
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from 'react-eva-icons';

import checkmark from '@coursera/assets/icons/check-mark.svg';
import wrongIcon from '@coursera/assets/icons/wrong-icon.svg';

import { CircleModal, LinearNavigation } from '../index';

import styles from './matching.module.scss';

const cx = classnames.bind(styles);

const canvasWidth = 211;
const canvasHeight = 450;

let timeout;

const MatchingCard = React.forwardRef(
  (
    {
      color,
      defaultOpen,
      index,
      isComplete,
      isCurrent,
      onHeight,
      onHover,
      onSelect,
      onToggle,
      question,
      text,
      top,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const classes = {
      card: true,
      highlight: isComplete || isCurrent,
      hidden: !isOpen,
    };

    const toggle = () => {
      if (onToggle) {
        setTimeout(() => onToggle(!isOpen), 0);
      }
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      if (onHeight) {
        const height = ref.current.getBoundingClientRect().height;
        const margin = window.getComputedStyle(ref.current).getPropertyValue('margin-bottom');
        onHeight(height + parseInt(margin, 10));
      }
    }, [onHeight, isOpen, ref]);

    return (
      <div
        ref={ref}
        className={cx(classes)}
        style={{ borderColor: color, top, zIndex: index }}
        onMouseEnter={() => onHover && onHover(ref, true)}
        onMouseLeave={() => onHover && onHover(ref, false)}
        onClick={onSelect}
      >
        <div className={styles.content}>
          <span>{text}</span>
          {isCurrent && <div className={styles.marker} />}
          {isComplete && (
            <div className={styles.controls}>
              <img src={checkmark} alt="checkmark" className={styles.icon} />
              <button
                className={styles.more}
                onClick={toggle}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Hide categorized items' : 'Show categorized items'}
              >
                <Icon size="xlarge" name={'arrow-ios-downward-outline'} fill={'#979797'} />
              </button>
            </div>
          )}
        </div>
        {question && <div className={styles.description}>{question.text}</div>}
      </div>
    );
  },
);

MatchingCard.displayName = 'MatchingCard';

MatchingCard.propTypes = {
  color: PropTypes.string,
  defaultOpen: PropTypes.bool,
  index: PropTypes.number,
  isCurrent: PropTypes.bool,
  isComplete: PropTypes.bool,
  onHeight: PropTypes.func,
  onHover: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  question: PropTypes.shape({ text: PropTypes.string }),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  top: PropTypes.number,
};

MatchingCard.defaultProps = {
  defaultOpen: false,
  index: 1,
};

const Matching = ({ exercises, onComplete, onProgress, titleLeft, titleRight, mobileSubtitle }) => {
  const [height, setHeight] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [answerFeedback, setAnswerFeedback] = useState('');

  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);

  const [questions, setQuestions] = useState(exercises[exerciseIndex].questions);
  const [answers, setAnswers] = useState(exercises[exerciseIndex].answers);

  const [transition, setTransition] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [allDone, setAllDone] = useState(false);

  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const questionContainerRef = useRef(null);

  const questionRefs = useMemo(() => ({}), []);
  const answerRefs = useMemo(() => ({}), []);

  const isMobile = useMediaQuery('only screen and (max-width: 790px)');

  const setRef = (refs, id) => refs[id] || (refs[id] = createRef());

  const calcProgress = () => {
    let completedCount = 0;
    let total = 0;

    exercises.forEach((e) =>
      e.questions.forEach((q) => ++total && q.completed && ++completedCount),
    );

    onProgress(completedCount / total);
  };
  const clearCanvas = () =>
    canvasRef.current.getContext('2d').clearRect(0, 0, canvasWidth, canvasHeight);

  const closeModal = () => {
    setModalOpen(false);
  };

  const draw = (ctx, start, end, color = '#dadce0') => {
    ctx.beginPath();
    ctx.moveTo(0, start);
    ctx.bezierCurveTo(canvasWidth * 0.63, start, canvasWidth * 0.37, end, canvasWidth, end);
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.stroke();
  };

  const getPoints = (questionRef, answerRef) => {
    const parentPos = wrapperRef.current?.getBoundingClientRect();
    const questionPos = questionRef.current?.getBoundingClientRect();
    const answerPos = answerRef.current?.getBoundingClientRect();

    const start = questionPos?.top - parentPos?.top + questionPos?.height / 2;
    const end = answerPos?.top - parentPos?.top + answerPos?.height / 2;

    return { start, end };
  };

  const getQuestionColor = (qRef) =>
    // don't use 'border-color' -- doesn't work in Firefox for some reason
    qRef.current && window.getComputedStyle(qRef.current).getPropertyValue('border-bottom-color');

  const renderLines = useCallback(
    (answerRef, on) => {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      clearCanvas();

      // for each complete question, find the start (question ref), then find the end (answer ref)
      questions.forEach((q) => {
        if (q.completed) {
          const aRef = answerRefs[q.answer];
          const qRef = questionRefs[q.id];
          const { start, end } = getPoints(qRef, aRef);
          const color = getQuestionColor(qRef);

          draw(context, start, end, color);

          if (aRef.current) {
            aRef.current.style.borderColor = color;
          }
        }
      });

      // on hover, draw a grey line using the current question and answer
      if (on) {
        const { start, end } = getPoints(questionRefs[questions[questionIndex].id], answerRef);
        draw(context, start, end);
      }
    },
    [answerRefs, questionIndex, questionRefs, questions],
  );

  const selectAnswer = (answer) => {
    const question = questions.find((q) => answer.id === q.answer);

    if (question.completed || !answer) {
      return;
    }

    // flow ends if answer is wrong
    if (questions[questionIndex].answer !== answer.id) {
      setModalOpen(true);
      setAnswerFeedback(answer.feedback);
      return;
    }

    answer.color = getQuestionColor(questionRefs[question.id]);

    questions[questionIndex].completed = true;

    // lets the container know how much is completed
    calcProgress();

    const nextIndex = questionIndex + 1;

    for (let i = nextIndex; questions.length; i++) {
      if (!questions[i]) {
        i = -1;
        continue;
      }

      // if this is true, all the questions are complete
      if (i === questionIndex) {
        runAnimation();
        setQuestionIndex(null);
        break;
      }

      // set next question to active
      if (!questions[i].completed) {
        setQuestionIndex(i);
        break;
      }
    }

    if (isMobile) {
      setTransition(true);

      setMobilePositions();

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        setTransition(false);
      }, 1000);
    }
  };

  const setMobilePositions = useCallback(() => {
    // initial top position of tiles
    let total = 53;
    let index = 1;

    // In order to get the effect that we want, the uncompleted answers are sorted by *answers* array
    // but the completed answers are sorted by *questions* array. Weird right?
    answers.forEach((a) => {
      const q = questions.find(propEq('answer', a.id));
      if (!q.completed) {
        a.index = index;
        a.mobileTop = total;
        total += a.mobileHeight;
        index++;
      }
    });

    questions.forEach((q) => {
      const a = answers.find(propEq('id', q.answer));
      if (q.completed) {
        a.index = index;
        a.mobileTop = total;
        total += a.mobileHeight;
        index++;
      }
    });

    setHeight(total);
  }, [answers, questions]);

  const selectQuestion = (question) => {
    const index = findIndex(propEq('id', question.id))(questions);
    setQuestionIndex(index);
  };

  const checkAllDone = () => {
    if (exerciseIndex + 1 === exercises.length && onComplete) {
      setAllDone(true);
      onComplete();
    }
  };

  // This triggers the sorting animation at the end of the exercise.
  const runAnimation = () => {
    // don't run if mobile
    if (isMobile) {
      exercises[exerciseIndex].completed = true;
      checkAllDone();
      return;
    }

    setTransition(true);

    const delay = 1000;
    const parentPos = wrapperRef.current.getBoundingClientRect();

    questions.forEach((q) => {
      const aRef = answerRefs[q.answer];
      const answer = answers.find((a) => a.id === q.answer);
      const answerPos = aRef.current.getBoundingClientRect();
      const currentPlacement = answerPos.top - parentPos.top;
      const { start } = getPoints(questionRefs[q.id], aRef);

      // adds a top so it looks like the answers don't move even though they are now position absolute
      aRef.current.style.top = `${currentPlacement}px`;

      // this delay is for the user to see the finished exercise, then answers are sorted by question
      setTimeout(() => {
        // clear the canvas
        clearCanvas();
        // set the new position of the answer card
        answerRefs[q.answer].current.style.top = `${start - 32}px`;
        // save the top to answer object for future rendering
        answer.top = start - 32;
      }, delay);
    });

    // this delay is to only render lines after the css transition is completed
    setTimeout(() => {
      exercises[exerciseIndex].completed = true;

      // The "someMissing" check is to see if React repainted the dom while the animation is running.
      // For example, if the user clicks the back button while the blocks are sorting. In that case,
      // we would lose the pointer to the refs.
      const someMissing = questions.find((q) => !questionRefs[q.id].current);

      if (!someMissing) {
        renderLines();
        // make sure to mark "complete" after the lines are rendered
        setTimeout(() => {
          setCompleted(true);
          checkAllDone();
        }, 0);
      }
    }, delay + 1000);
  };

  const prevExercise = () => {
    if (exerciseIndex === 0) {
      return;
    }
    setExerciseIndex(exerciseIndex - 1);
  };

  const nextExercise = () => {
    if (exerciseIndex === exercises.length - 1 || !exercises[exerciseIndex].completed) {
      return;
    }
    setExerciseIndex(exerciseIndex + 1);
  };

  const isLast = () => !exercises[exerciseIndex + 1];

  const calcAnswerTop = (answer) => {
    if (isMobile) {
      return answer.mobileTop;
    }

    if (completed) {
      return answer.top;
    }
  };

  useEffect(() => {
    const func = () => isMobile || renderLines();
    func();
    window.addEventListener('resize', func);
    return () => window.removeEventListener('resize', func);
  }, [isMobile, renderLines]);

  useEffect(() => {
    const currentExercise = exercises[exerciseIndex];
    setCompleted(!!currentExercise.completed);
    setTransition(false);
    setQuestions(currentExercise.questions);
    setAnswers(currentExercise.answers);
  }, [exerciseIndex, exercises]);

  useEffect(() => {
    setCompleted(false);
  }, []);

  useEffect(() => {
    setQuestionIndex(findIndex((q) => !q.completed, questions));

    if (isMobile) {
      setMobilePositions();
    }
  }, [answers, isMobile, questions, setMobilePositions]);

  return (
    <section className={styles.section}>
      <div className={styles.message}>
        {exercises[exerciseIndex].completed ? (
          <div className={cx({ instruct: true })}>
            {isLast() ? (
              'You’ve categorized everything.'
            ) : (
              <>
                Select <b>Next</b> to continue
              </>
            )}
          </div>
        ) : (
          isMobile && (
            <>
              <div className={styles.subtitle}>{mobileSubtitle}</div>
              {questions.map((q, i) => (
                <div key={q.id} className={cx({ question: true, show: i === questionIndex })}>
                  {q.text}
                </div>
              ))}
            </>
          )
        )}
      </div>
      <div className={cx({ wrapper: true, completed, transition })} ref={wrapperRef}>
        <div className={styles.questions} ref={questionContainerRef}>
          <div className={styles.title}>{titleLeft}</div>
          {questions.map((question) => {
            const isCurrent =
              questions[questionIndex] && question.id === questions[questionIndex].id;

            return (
              <MatchingCard
                ref={setRef(questionRefs, question.id)}
                key={question.id}
                text={question.text}
                isComplete={question.completed}
                isCurrent={isCurrent && !question.completed}
                onSelect={() => selectQuestion(question)}
              />
            );
          })}
        </div>

        <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />

        <div className={styles.answers} style={{ minHeight: isMobile ? height : 0 }}>
          <div className={styles.title}>{titleRight}</div>
          {answers.map((answer) => {
            const question = questions.find((q) => answer.id === q.answer);

            const onToggle = (isOpen) => {
              answer.open = isOpen;
              setMobilePositions();
            };

            return (
              <MatchingCard
                ref={setRef(answerRefs, answer.id)}
                key={answer.id}
                answer={answer}
                color={completed ? answer.color : undefined}
                index={answer.index}
                isComplete={question.completed}
                defaultOpen={answer.open}
                onHeight={(height) => (answer.mobileHeight = height)}
                onHover={question.completed ? undefined : renderLines}
                onSelect={() => selectAnswer(answer)}
                onToggle={onToggle}
                question={question.completed && question}
                text={answer.text}
                top={calcAnswerTop(answer)}
              />
            );
          })}
        </div>
      </div>
      <div className={cx({ nav: true, done: allDone })}>
        <LinearNavigation
          item={exercises[exerciseIndex]}
          allItems={exercises}
          isStrict={true}
          onNext={nextExercise}
          onBack={prevExercise}
        />
      </div>
      <CircleModal
        open={modalOpen}
        appElementId="root"
        ctaText="Try again"
        closeModal={closeModal}
        icon={wrongIcon}
        requestClose={closeModal}
      >
        <div>
          <h6>Incorrect.</h6>
          <p>{answerFeedback}</p>
        </div>
      </CircleModal>
    </section>
  );
};

Matching.propTypes = {
  exercises: PropTypes.arrayOf(
    PropTypes.shape({
      answers: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
        }),
      ),
      completed: PropTypes.bool,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          answer: PropTypes.string,
          color: PropTypes.string,
          completed: PropTypes.bool,
          id: PropTypes.string,
          text: PropTypes.string,
        }),
      ),
    }),
  ),
  onComplete: PropTypes.func,
  onProgress: PropTypes.func,
  titleLeft: PropTypes.string,
  titleRight: PropTypes.string,
  mobileSubtitle: PropTypes.string,
};

Matching.reset = (exercises) => {
  exercises.forEach((e) => {
    delete e.completed;
    e.questions.forEach((q) => delete q.completed);
    e.answers.forEach((a) => {
      delete a.color;
      delete a.mobileTop;
      delete a.open;
      delete a.top;
    });
  });
};

export default Matching;
